import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { api } from '../api';
import { AccountClassification, COA, EntityAccount } from '../types/entity';

async function getCharts(entityId: string): Promise<{ charts: COA[] }> {
    const { data } = await api.get(`/entities/${entityId}/charts`);
    return data;
}

export function useCOAs(entityId: string, { enabled }: { enabled: boolean }): UseQueryResult<{ charts: COA[] }, unknown> {
    return useQuery(['entities', entityId, 'charts'], () => getCharts(entityId), { enabled });
}

type GetEntityAccountsParams = {
    page?: number;
    limit?: number;
    search?: string;
    sort?: {
        dir: 'asc' | 'desc';
        field: 'code';
    };
    filter?: {
        class?: AccountClassification;
        canJournal?: boolean;
    };
};

type AccountsResponse = {
    count: number;
    items: EntityAccount[];
};

async function getAccounts(entityId: string, chartId: string, params: GetEntityAccountsParams): Promise<AccountsResponse> {
    const { data } = await api.get(`/entities/${entityId}/charts/${chartId}/accounts`, {
        params: {
            ...(params?.filter ?? {}),
            search: params?.search,
            'sort[dir]': params?.sort?.dir,
            'sort[field]': params?.sort?.field,
        },
    });
    return data;
}

export function useEntityAccounts(
    entityId: string,
    chartId: string,
    params: GetEntityAccountsParams = {}
): UseQueryResult<AccountsResponse, unknown> {
    return useQuery(['entities', entityId, 'charts', chartId, 'accounts', params], () => getAccounts(entityId, chartId, params));
}

/**
 * Request a csv of the accounts for a chart and automatically trigger a download.
 * @param entityId
 * @param chartId
 */
export async function download(entityId: string, chartId: string): Promise<void> {
    const response = await api.get(`/entities/${entityId}/charts/${chartId}/accounts/download`, {
        responseType: 'blob',
        headers: { Accept: 'text/csv' },
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'accounts.csv');
    document.body.appendChild(link);
    link.click();
}

export function useDownloadCOA(entityId: string, chartId: string): UseMutationResult<unknown, AxiosError, unknown, unknown> {
    return useMutation(() => download(entityId, chartId));
}
